import { useState, type ReactNode } from 'react'
import { Minus, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { formatCurrency } from '../../utils/format-currency'

interface ItemRowProps {
  id: string
  quantity: number
  name: string
  unitPrice?: number | null
  meta?: ReactNode
  detailContent?: ReactNode
  onIncrease?: () => void
  onDecrease?: () => void
  onRemove?: () => void
  disableIncrease?: boolean
  disableDecrease?: boolean
  disableActions?: boolean
}

export function ItemRow({
  quantity,
  name,
  unitPrice,
  meta,
  detailContent,
  onIncrease,
  onDecrease,
  onRemove,
  disableIncrease,
  disableDecrease,
  disableActions,
}: ItemRowProps) {
  const [expanded, setExpanded] = useState(false)
  const hasDetails = Boolean(detailContent)
  const canAdjust = Boolean(onIncrease || onDecrease)
  const showRemove = Boolean(onRemove)

  return (
    <div className="py-3 flex items-start justify-between gap-3 group/row">
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[color:var(--color-text)] truncate">
              {quantity} × {name}
            </p>
            {typeof unitPrice === 'number' && (
              <p className="text-xs text-muted mt-0.5">{formatCurrency(unitPrice)}</p>
            )}
          </div>
          {canAdjust && (
            <div className="flex items-center gap-1 rounded-full border border-divider bg-surface p-1 shadow-sm transition hover:shadow-md">
              <button
                type="button"
                onClick={onDecrease}
                disabled={disableActions || disableDecrease}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-neutral-800 disabled:opacity-30 transition-colors"
                aria-label="Disminuir cantidad"
              >
                <Minus className="h-3.5 w-3.5" />
              </button>
              <span className="w-6 text-center text-xs font-bold text-[color:var(--color-text)]" aria-hidden="true">
                {quantity}
              </span>
              <button
                type="button"
                onClick={onIncrease}
                disabled={disableActions || disableIncrease}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 dark:hover:bg-neutral-800 disabled:opacity-30 transition-colors"
                aria-label="Aumentar cantidad"
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
        {meta && <div className="mt-2 flex flex-wrap gap-2 text-xs text-[color:var(--color-text)]">{meta}</div>}
        {hasDetails && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              aria-expanded={expanded}
            >
              {expanded ? 'Ocultar detalles' : 'Ver detalles'}
              {expanded ? (
                <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </button>
            {expanded && (
              <div className="mt-2 rounded-xl border border-divider bg-slate-50/70 dark:bg-neutral-800/40 p-2.5 space-y-1 text-xs text-slate-700 dark:text-neutral-200 font-medium" aria-live="polite">
                {detailContent}
              </div>
            )}
          </div>
        )}
      </div>
      {showRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disableActions}
          className="mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-divider text-slate-400 hover:text-red-650 hover:bg-red-50 hover:border-red-200 dark:hover:text-red-400 dark:hover:bg-red-950/20 dark:hover:border-red-900/35 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Eliminar ítem"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

export default ItemRow
